<template>
  <div class="tw-w-full tw-flex tw-flex-col tw-gap-3">
    <div
      v-if="title"
      class="tw-text-body-md-mobile apTablet2:tw-text-body-md-desktop "
      v-html="title"
    />
    <div class="tw-bg-white apDesktop:tw-rounded-xl tw-rounded-[10px] tw-relative">
      <input
        :key="attachmentKey"
        ref="fileInput"
        type="file"
        class="tw-hidden"
        accept=".pdf,.zip,.rar"
        @change="handleFileChange"
      >
      <input
        type="text"
        :class="[
          'tw-w-full tw-h-[60px]',
          'tw-outline tw-outline-offset-[-2px] tw-outline-1 tw-outline-ap-gainsboro ',
          // 'dark:tw-outline-ap-night-rider dark:tw-text-white',
          'focus:tw-outline-ap-dodger-blue focus:tw-outline-2',
          'apDesktop:tw-rounded-xl tw-rounded-[10px] tw-text-body-md-mobile apTablet2:tw-text-body-md-desktop  tw-placeholder-black',
          'tw-pl-6 tw-py-4 tw-pr-[60px]',
        ]"
        :placeholder="placeholder"
        :value="filename"
        readonly
        :style="[paperClipIcon, {
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '95% center',
          cursor: 'pointer',
        }]"
        @click="triggerFileInputClick"
      >
    </div>
    <span class="tw-text-label-md-mobile apTablet2:tw-text-label-md-desktop tw-text-ap-nobel">
      If you have multiple files, please ZIP them up first. Max 10MB.
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { uuid } from 'vue3-uuid';
import { cdn } from '../../support/cdn';
import { getDarkMode } from '../../support/helper';

const isDarkMode = ref(false);

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  file: {
    type: File,
    required: false,
  },
  placeholder: {
    type: String,
    default: 'Upload PDF',
  },
});

const emit = defineEmits(['submit', 'update:file']);

const filename = ref('Upload PDF');
const fileInput = ref<HTMLInputElement | null>(null);
const maxFileSize = 10 * 1024 * 1024; // 10MB

const attachmentKey = ref(uuid.v4());

onMounted(() => {
  isDarkMode.value = getDarkMode();

  if (props.file) {
    filename.value = props.file.name;
  }
});

const paperClipIcon = computed(() => {
  const baseUrl = 'icons/contact-us/';
  const icon = props.file
    ? 'icon_thrash_can.svg'
    : 'icon_paper_clip.svg';
  return {
    background: `url(${cdn(baseUrl)}${icon})`,
  };
});

// Trigger file input click
const triggerFileInputClick = () => {
  if (props.file) {
    clearFile();
  } else {
    fileInput.value?.click();
  }
};

// Handle file selection
const handleFileChange = (event: Event) => {
  const inputElement = event.target as HTMLInputElement;
  const files = inputElement.files;
  if (files && files[0]) {
    const file = files[0];
    if (file.size > maxFileSize) {
      filename.value = 'Upload PDF';
      alert('File size limit is 10MB.');
      return;
    } else {
      filename.value = file.name;
    }
  } else {
    filename.value = 'Upload PDF';
  }
  emit('update:file', files ? files[0] : null);
};

// Clear selected file
const clearFile = () => {
  fileInput.value!.value = ''; // Clear file input
  filename.value = 'Upload PDF'; // Reset displayed filename
  attachmentKey.value = uuid.v4();
  emit('update:file', null);
};
</script>
